import { useState } from "react";
import { Plus, Tag, Pencil, ToggleLeft, ToggleRight, GitBranch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useExpenseCategories, type ExpenseCategory } from "@/hooks/useExpenseCategories";
import { useChartOfAccounts, ACCOUNT_TYPE_LABELS } from "@/hooks/useChartOfAccounts";
import { ExpenseCategoryFormDialog } from "@/components/expenses/ExpenseCategoryFormDialog";

export default function CategoriaDespesasPage() {
  const {
    allCategories,
    isLoading,
    createCategory,
    updateCategory,
    toggleCategoryActive,
  } = useExpenseCategories();
  const { accounts } = useChartOfAccounts();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | null>(null);

  const handleCreate = () => {
    setSelectedCategory(null);
    setDialogOpen(true);
  };

  const handleEdit = (category: ExpenseCategory) => {
    setSelectedCategory(category);
    setDialogOpen(true);
  };

  const handleSubmit = async (data: { name: string; chart_account_id: string | null; is_active?: boolean }) => {
    if (selectedCategory) {
      await updateCategory(selectedCategory.id, data);
    } else {
      await createCategory(data);
    }
  };

  const getAccountName = (accountId: string | null) => {
    if (!accountId) return null;
    const account = accounts.find((a) => a.id === accountId);
    return account ? `${account.code} - ${account.name}` : null;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Tag className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Categorias de Despesa</h1>
            <p className="text-sm text-muted-foreground">
              Gerencie as categorias e vincule ao Plano de Contas para o DRE
            </p>
          </div>
        </div>
        <Button onClick={handleCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          Nova Categoria
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Categorias Cadastradas
          </CardTitle>
          <CardDescription>
            Vincule cada categoria a uma conta do Plano de Contas para classificação automática no DRE
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : allCategories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma categoria cadastrada. Clique em "Nova Categoria" para começar.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Conta do Plano</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allCategories.map((category) => {
                  const accountName = getAccountName(category.chart_account_id);
                  return (
                    <TableRow key={category.id}>
                      <TableCell className="font-medium">{category.name}</TableCell>
                      <TableCell>
                        {accountName ? (
                          <span className="text-sm">{accountName}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground italic">Não vinculada</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={category.is_active ? "default" : "secondary"}>
                          {category.is_active ? "Ativa" : "Inativa"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(category)}
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => toggleCategoryActive(category.id, !category.is_active)}
                            title={category.is_active ? "Desativar" : "Ativar"}
                          >
                            {category.is_active ? (
                              <ToggleRight className="h-4 w-4 text-green-600" />
                            ) : (
                              <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <ExpenseCategoryFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        category={selectedCategory}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
